import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { makeAutoObservable } from 'mobx';
import {
    DropdownSelect,
    type SingleValue,
    type OptionProps,
    type GroupBase,
} from 'src/components/DropdownWrapper/DropdownWrapper';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FormInputState as FormInputStateOld } from 'src_common/common/mobx-utils/Form';
import {
    SelectWrapper,
    SelectDropdownWrapper,
    Option,
    SelectedValue,
    CheckIconWrapper,
    ChevronIconWrapper,
    getSelectStyles,
} from 'src/domains/layouts/shared/formComponents/Select/components/new/Select.style';
import { InputError, InputLabel } from 'src/domains/layouts/shared/formComponents/FormComponents.style';

export interface SelectOptionType {
    value: string;
    label: string;
}

interface SelectIconOptionType {
    value: string;
    element: JSX.Element;
}

class SelectState {
    public value: SingleValue<SelectOptionType> = null;

    public constructor(
        private readonly stateValue: FormInputState<string, string> | FormInputStateOld<string> | string,
        private readonly onChangeCB: (value: string) => void,
        private readonly showCheckmark: boolean = true,
        private readonly iconsList: Array<SelectIconOptionType> = []
    ) {
        makeAutoObservable(this);
    }

    public get isFilled(): boolean {
        return this.value !== null;
    }

    public get selectValue(): string | null {
        if (this.stateValue instanceof FormInputState || this.stateValue instanceof FormInputStateOld) {
            return this.stateValue.value;
        }
        return this.stateValue;
    }

    public get shouldShowCheckmark(): boolean {
        return this.showCheckmark;
    }

    public renderIcon = (option: SelectOptionType): JSX.Element | null => {
        const intersection = this.iconsList.filter((element) => option.value === element.value);
        const optionIcon = intersection[0]?.element ?? null;

        return optionIcon;
    };

    public onChange = (option: SingleValue<SelectOptionType>): void => {
        if (option === null) {
            console.error('Select onChange error');
            return;
        }

        this.onChangeCB(option.value);

        this.value = option;

        if (this.stateValue instanceof FormInputState || this.stateValue instanceof FormInputStateOld) {
            this.stateValue.setValue(option.value);
        }
    };

    public get errorMessage(): string | undefined {
        if (this.stateValue instanceof FormInputState) {
            return this.stateValue.errorsForView[0];
        }
        return;
    }
}

interface FilterDropdownPropsType {
    selectOptions: Array<SelectOptionType>;
    stateValue: FormInputState<string, string> | FormInputStateOld<string> | string;
    onChangeCB: (value: string) => void;
    iconsList?: Array<SelectIconOptionType>;
    placeholder?: string;
    label?: string;
    showCheckmark?: boolean;
    dataTest?: string;
}

interface CustomSingleValuePropsType {
    data: SelectOptionType;
}

export const Select = observer(
    'Select',
    ({
        selectOptions,
        stateValue,
        onChangeCB,
        iconsList,
        placeholder,
        label,
        showCheckmark,
        dataTest,
    }: FilterDropdownPropsType) => {
        const value = typeof stateValue === 'string' ? stateValue : stateValue.value;

        const [selectionState] = React.useState(
            () => new SelectState(stateValue, onChangeCB, showCheckmark, iconsList)
        );

        const foundOption = selectOptions.find((opt) => opt.value === value);
        const selectedOption = foundOption === undefined ? null : foundOption;

        const CustomOption = (
            optionProps: OptionProps<SelectOptionType, false, GroupBase<SelectOptionType>>
        ): JSX.Element => {
            const { data, innerRef, innerProps, isSelected, isFocused } = optionProps;

            return (
                <Option
                    ref={innerRef}
                    {...innerProps}
                    isSelected={isSelected}
                    isFocused={isFocused}
                    role='option'
                    aria-selected={isSelected}
                    data-test='select-option'
                >
                    {selectionState.renderIcon(data)}
                    {data.label}
                    {selectionState.shouldShowCheckmark && isSelected ? <CheckIconWrapper /> : null}
                </Option>
            );
        };

        const CustomSingleValue = (props: CustomSingleValuePropsType): JSX.Element => {
            const { data } = props;

            return (
                <SelectedValue>
                    {selectionState.renderIcon(data)}
                    {data.label}
                </SelectedValue>
            );
        };

        return (
            <SelectWrapper data-test={dataTest}>
                {label === undefined ? null : <InputLabel>{label}</InputLabel>}
                <SelectDropdownWrapper isFilled={selectionState.isFilled}>
                    <DropdownSelect<SelectOptionType>
                        options={selectOptions}
                        value={selectedOption}
                        onChange={selectionState.onChange}
                        placeholder={placeholder}
                        isClearable={false}
                        isSearchable={false}
                        customStyles={getSelectStyles<SelectOptionType, false>(selectionState.isFilled)}
                        components={{
                            Option: CustomOption,
                            SingleValue: CustomSingleValue,
                        }}
                        getOptionValue={(option) => option.value}
                        getOptionLabel={(option) => option.label}
                    />
                    <ChevronIconWrapper
                        position='down'
                        isFilled={selectionState.isFilled}
                    />
                </SelectDropdownWrapper>
                {selectionState.errorMessage === undefined ? null : (
                    <InputError>{selectionState.errorMessage}</InputError>
                )}
            </SelectWrapper>
        );
    }
);
